import React, { useState, useEffect, useMemo } from 'react';
import Input from './ui/Input';
import Button from './ui/Button';
import { useToast } from './ui/Toast';
import './VariantMatrix.css';

/**
 * VariantMatrix Component
 * Industry-standard variant matrix for product variations
 *
 * Structure:
 * {
 *   attributes: [{ name: "Size", values: ["S", "M", "L"] }, { name: "Color", values: ["Red", "Blue"] }],
 *   variants: [
 *     { id: "var_001", combination: { Size: "S", Color: "Red" }, price: 100, sku: "...", quantity: 50, enabled: true },
 *     ...
 *   ]
 * }
 */

// Generate unique ID
const generateId = () => `var_${Date.now().toString(36)}_${Math.random().toString(36).substr(2, 5)}`;

// Generate cartesian product of arrays
const cartesianProduct = (arrays) => {
  if (arrays.length === 0) return [[]];
  if (arrays.length === 1) return arrays[0].map(item => [item]);

  return arrays.reduce((acc, curr, idx) => {
    if (idx === 0) return curr.map(item => [item]);
    const result = [];
    acc.forEach(a => {
      curr.forEach(b => {
        result.push([...a, b]);
      });
    });
    return result;
  }, []);
};

// Generate all variant combinations from attributes
const generateVariantCombinations = (attributes, basePrice = 0, baseQuantity = 0, existingVariants = []) => {
  if (!attributes || attributes.length === 0) return [];

  const validAttributes = attributes.filter(attr =>
    attr && attr.name && attr.values && attr.values.length > 0
  );

  if (validAttributes.length === 0) return [];

  const combos = cartesianProduct(
    validAttributes.map(attr => attr.values.map(v => ({ attrName: attr.name, value: v })))
  );

  // Helper to create a key from combination for matching
  const getCombinationKey = (combination) => {
    return Object.entries(combination)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}:${v}`)
      .join('|');
  };

  // Create a map of existing variants by their combination key
  const existingMap = new Map();
  existingVariants.forEach(v => {
    if (v.combination) {
      existingMap.set(getCombinationKey(v.combination), v);
    }
  });

  return combos.map(combo => {
    const combination = {};
    combo.forEach(item => {
      combination[item.attrName] = item.value;
    });

    const combinationKey = getCombinationKey(combination);
    const existing = existingMap.get(combinationKey);

    // Preserve existing variant data if it exists
    if (existing) {
      return {
        ...existing,
        combination // Use new combination object in case attribute order changed
      };
    }

    // Create new variant with defaults
    return {
      id: generateId(),
      combination,
      price: basePrice,
      compareAtPrice: null,
      sku: '',
      quantity: baseQuantity,
      enabled: true
    };
  });
};

// Format combination for display
const formatCombination = (combination) => {
  if (!combination) return '';
  return Object.values(combination).join(' / ');
};

const VariantMatrix = ({
  value = { attributes: [], variants: [] },
  onChange,
  basePrice = 0,
  baseQuantity = 0,
  baseSku = '',
  attributeLimit = null,
  disabled = false
}) => {
  const toast = useToast();

  // State for new attribute input
  const [newAttrName, setNewAttrName] = useState('');
  const [newAttrValues, setNewAttrValues] = useState('');

  // Ensure value has proper structure
  const attributes = value?.attributes || [];
  const variants = value?.variants || [];

  // Calculate total variants
  const totalVariants = useMemo(() => {
    if (attributes.length === 0) return 0;
    return attributes.reduce((acc, attr) => acc * (attr.values?.length || 1), 1);
  }, [attributes]);

  // Add new attribute
  const handleAddAttribute = () => {
    if (!newAttrName.trim() || !newAttrValues.trim()) return;

    // Check attribute limit
    if (attributeLimit !== null && attributes.length >= attributeLimit) {
      toast.warning(`Your plan allows a maximum of ${attributeLimit} variation attributes. Upgrade to add more.`);
      return;
    }

    const values = newAttrValues.split(',').map(v => v.trim()).filter(Boolean);
    if (values.length === 0) return;

    // Check if attribute name already exists
    if (attributes.some(a => a.name.toLowerCase() === newAttrName.trim().toLowerCase())) {
      toast.error('An attribute with this name already exists');
      return;
    }

    const newAttributes = [
      ...attributes,
      { name: newAttrName.trim(), values }
    ];

    // Regenerate variants, preserving existing data
    const newVariants = generateVariantCombinations(newAttributes, parseFloat(basePrice) || 0, parseInt(baseQuantity) || 0, variants);

    onChange({
      attributes: newAttributes,
      variants: newVariants
    });

    setNewAttrName('');
    setNewAttrValues('');
  };

  // Remove attribute
  const handleRemoveAttribute = (attrIndex) => {
    const newAttributes = attributes.filter((_, idx) => idx !== attrIndex);
    const newVariants = newAttributes.length > 0
      ? generateVariantCombinations(newAttributes, parseFloat(basePrice) || 0, parseInt(baseQuantity) || 0, variants)
      : [];

    onChange({
      attributes: newAttributes,
      variants: newVariants
    });
  };

  // Add value to existing attribute
  const handleAddAttributeValue = (attrIndex, newValue) => {
    if (!newValue.trim()) return;

    const attr = attributes[attrIndex];
    if (attr.values.includes(newValue.trim())) return;

    const newAttributes = attributes.map((a, idx) => {
      if (idx === attrIndex) {
        return { ...a, values: [...a.values, newValue.trim()] };
      }
      return a;
    });

    const newVariants = generateVariantCombinations(newAttributes, parseFloat(basePrice) || 0, parseInt(baseQuantity) || 0, variants);

    onChange({
      attributes: newAttributes,
      variants: newVariants
    });
  };

  // Remove value from attribute
  const handleRemoveAttributeValue = (attrIndex, valueIndex) => {
    const attr = attributes[attrIndex];
    if (attr.values.length <= 1) {
      // If only one value left, remove the entire attribute
      handleRemoveAttribute(attrIndex);
      return;
    }

    const newAttributes = attributes.map((a, idx) => {
      if (idx === attrIndex) {
        return { ...a, values: a.values.filter((_, vIdx) => vIdx !== valueIndex) };
      }
      return a;
    });

    const newVariants = generateVariantCombinations(newAttributes, parseFloat(basePrice) || 0, parseInt(baseQuantity) || 0, variants);

    onChange({
      attributes: newAttributes,
      variants: newVariants
    });
  };

  // Update variant field
  const handleUpdateVariant = (variantId, field, newValue) => {
    const newVariants = variants.map(v => {
      if (v.id === variantId) {
        return { ...v, [field]: newValue };
      }
      return v;
    });

    onChange({
      attributes,
      variants: newVariants
    });
  };

  // Toggle variant enabled
  const handleToggleVariant = (variantId) => {
    handleUpdateVariant(variantId, 'enabled', !variants.find(v => v.id === variantId)?.enabled);
  };

  // Bulk set price for all variants
  const handleBulkSetPrice = () => {
    const newPrice = prompt('Enter price for all variants:', basePrice);
    if (newPrice === null) return;

    const price = parseFloat(newPrice) || 0;
    const newVariants = variants.map(v => ({ ...v, price }));

    onChange({
      attributes,
      variants: newVariants
    });
  };

  // Bulk set quantity for all variants
  const handleBulkSetQuantity = () => {
    const qty = prompt('Enter quantity for all variants:', '0');
    if (qty === null) return;

    const quantity = parseInt(qty) || 0;
    const newVariants = variants.map(v => ({ ...v, quantity }));

    onChange({
      attributes,
      variants: newVariants
    });
  };

  // Auto-generate SKUs
  const handleAutoGenerateSKUs = () => {
    const prefix = baseSku || 'SKU';
    const newVariants = variants.map(v => ({
      ...v,
      sku: `${prefix}-${Object.values(v.combination).map(val =>
        val.toString().substring(0, 3).toUpperCase()
      ).join('-')}`
    }));

    onChange({
      attributes,
      variants: newVariants
    });

    toast.success(`Generated SKUs for ${newVariants.length} variants`);
  };

  return (
    <div className="variant-matrix">
      {/* Attribute Limit Indicator */}
      {attributeLimit !== null && (
        <div className="attribute-limit-indicator">
          <span className={attributes.length >= attributeLimit ? 'at-limit' : ''}>
            {attributes.length} / {attributeLimit} attribute types used
          </span>
          {attributes.length >= attributeLimit && (
            <span className="limit-warning">Upgrade for more</span>
          )}
        </div>
      )}

      {/* Add Attribute Section */}
      <div className="add-attribute-section">
        <h4 className="section-title">Add Variation Attribute</h4>
        <div className="attribute-input-row">
          <div className="input-group">
            <label>Attribute Name</label>
            <Input
              value={newAttrName}
              onChange={(e) => setNewAttrName(e.target.value)}
              placeholder="e.g., Size, Color, Material"
              disabled={disabled || (attributeLimit !== null && attributes.length >= attributeLimit)}
            />
          </div>
          <div className="input-group flex-grow">
            <label>Values (comma-separated)</label>
            <Input
              value={newAttrValues}
              onChange={(e) => setNewAttrValues(e.target.value)}
              placeholder="e.g., Small, Medium, Large"
              disabled={disabled || (attributeLimit !== null && attributes.length >= attributeLimit)}
            />
          </div>
          <Button
            type="button"
            onClick={handleAddAttribute}
            disabled={disabled || !newAttrName.trim() || !newAttrValues.trim() || (attributeLimit !== null && attributes.length >= attributeLimit)}
            className="add-btn"
          >
            + Add
          </Button>
        </div>
      </div>

      {/* Attributes List */}
      {attributes.length > 0 && (
        <div className="attributes-section">
          <h4 className="section-title">Defined Attributes</h4>
          <div className="attributes-list">
            {attributes.map((attr, attrIndex) => (
              <div key={attrIndex} className="attribute-card">
                <div className="attribute-header">
                  <span className="attribute-name">{attr.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveAttribute(attrIndex)}
                    className="remove-btn"
                    disabled={disabled}
                  >
                    Remove
                  </button>
                </div>
                <div className="attribute-values">
                  {attr.values.map((val, valIndex) => (
                    <span key={valIndex} className="value-tag">
                      {val}
                      <button
                        type="button"
                        onClick={() => handleRemoveAttributeValue(attrIndex, valIndex)}
                        className="remove-value-btn"
                        disabled={disabled}
                      >
                        x
                      </button>
                    </span>
                  ))}
                  <AddValueInput
                    onAdd={(val) => handleAddAttributeValue(attrIndex, val)}
                    disabled={disabled}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Variant Matrix Table */}
      {variants.length > 0 && (
        <div className="variants-section">
          <div className="variants-header">
            <h4 className="section-title">
              Variant Combinations ({variants.length} variants)
            </h4>
            <div className="bulk-actions">
              <button type="button" onClick={handleBulkSetPrice} disabled={disabled}>
                Set All Prices
              </button>
              <button type="button" onClick={handleBulkSetQuantity} disabled={disabled}>
                Set All Qty
              </button>
              <button type="button" onClick={handleAutoGenerateSKUs} disabled={disabled}>
                Auto SKUs
              </button>
            </div>
          </div>

          <div className="variants-table-wrapper">
            <table className="variants-table">
              <thead>
                <tr>
                  <th className="col-enabled">Active</th>
                  <th className="col-variant">Variant</th>
                  <th className="col-price">Price</th>
                  <th className="col-sku">SKU</th>
                  <th className="col-qty">Quantity</th>
                </tr>
              </thead>
              <tbody>
                {variants.map(variant => (
                  <tr key={variant.id} className={!variant.enabled ? 'disabled-row' : ''}>
                    <td className="col-enabled">
                      <input
                        type="checkbox"
                        checked={variant.enabled}
                        onChange={() => handleToggleVariant(variant.id)}
                        disabled={disabled}
                      />
                    </td>
                    <td className="col-variant">
                      {formatCombination(variant.combination)}
                    </td>
                    <td className="col-price">
                      <input
                        type="number"
                        step="0.01"
                        value={variant.price || ''}
                        onChange={(e) => handleUpdateVariant(variant.id, 'price', parseFloat(e.target.value) || 0)}
                        disabled={disabled || !variant.enabled}
                        placeholder="0.00"
                      />
                    </td>
                    <td className="col-sku">
                      <input
                        type="text"
                        value={variant.sku || ''}
                        onChange={(e) => handleUpdateVariant(variant.id, 'sku', e.target.value)}
                        disabled={disabled || !variant.enabled}
                        placeholder="SKU"
                      />
                    </td>
                    <td className="col-qty">
                      <input
                        type="number"
                        value={variant.quantity || ''}
                        onChange={(e) => handleUpdateVariant(variant.id, 'quantity', parseInt(e.target.value) || 0)}
                        disabled={disabled || !variant.enabled}
                        placeholder="0"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {attributes.length === 0 && (
        <div className="empty-state">
          <p>No variations defined yet.</p>
          <p className="hint">Add attributes like Size, Color, or Material to create product variants.</p>
        </div>
      )}
    </div>
  );
};

// Helper component for adding values inline
const AddValueInput = ({ onAdd, disabled }) => {
  const [value, setValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const handleSubmit = () => {
    if (value.trim()) {
      onAdd(value.trim());
      setValue('');
    }
    setIsEditing(false);
  };

  if (!isEditing) {
    return (
      <button
        type="button"
        onClick={() => setIsEditing(true)}
        className="add-value-tag"
        disabled={disabled}
      >
        + Add
      </button>
    );
  }

  return (
    <input
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={handleSubmit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleSubmit();
        }
        if (e.key === 'Escape') {
          setValue('');
          setIsEditing(false);
        }
      }}
      placeholder="Value"
      className="add-value-input"
      autoFocus
      disabled={disabled}
    />
  );
};

export default VariantMatrix;
